import { addDays, subDays, min as earliestOf, max as latestOf } from "date-fns";
import { getTimezoneOffset } from "date-fns-tz";
import { COMPILATION_MAX_RANGE_DAYS, COMPILATION_MAX_RANGE_MS } from "../../../client-messages/compilation";

/**
 * The instant a picked date and an entered `"HH:MM"` denote in `timezone`.
 *
 * The pickers and the time fields describe a wall clock, and the wall clock a
 * viewer means is the camera's, not their own — a viewer in Sydney asking a
 * New Zealand camera for footage from 09:00 means 09:00 where the camera is.
 * Everything downstream (the range bounds, the clip count, the request) is
 * built from this, so the whole form agrees on which instants were meant.
 *
 * The viewer's own zone is never consulted, which rules out a class of error
 * that neither `setHours` nor `fromZonedTime` avoids: both resolve a wall clock
 * through the local zone, and on the viewer's spring-forward day the hour they
 * typed may not exist *there* — 02:30 silently rolls to 03:30, and the camera
 * is asked for an hour later than was entered, for a time that is perfectly
 * ordinary where the camera stands. The components are read off the picked date
 * and reassembled against UTC instead, then shifted by the camera zone's own
 * offset.
 *
 * The offset is looked up twice because it is itself a function of the instant:
 * the first pass finds the offset near the answer, the second finds it at the
 * answer, which is what settles a wall clock falling close to a transition in
 * the camera's zone.
 *
 * An incomplete time reads as the start of the picked day; the form blocks
 * submission until it is a complete `"HH:MM"` anyway, and the bounds below just
 * fall back to whole-day arithmetic in the meantime.
 */
export function zonedInstant(date: Date, time: string, timezone: string): Date {
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  const hours = match ? Number(match[1]) : 0;
  const minutes = match ? Number(match[2]) : 0;

  const asUtc = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes);

  const nearby = asUtc - getTimezoneOffset(timezone, new Date(asUtc));
  return new Date(asUtc - getTimezoneOffset(timezone, new Date(nearby)));
}

/**
 * The zone's offset at a given moment, as `"UTC+10"` / `"UTC+9:30"` / `"UTC"`.
 *
 * Derived from the offset rather than read from `Intl`'s short time zone name:
 * that name is ICU- and locale-dependent, and on the runtimes here yields
 * `"GMT+10"` for Sydney rather than the `"AEST"` it is often assumed to give.
 * An offset says the same thing in a form that cannot vary by platform.
 */
export function timezoneOffsetLabel(timezone: string, at: Date): string {
  const offsetMs = getTimezoneOffset(timezone, at);
  if (offsetMs === 0) return "UTC";

  const sign = offsetMs < 0 ? "-" : "+";
  const totalMinutes = Math.abs(offsetMs) / 60_000;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  return minutes === 0 ?
      `UTC${sign}${String(hours)}`
    : `UTC${sign}${String(hours)}:${String(minutes).padStart(2, "0")}`;
}

/**
 * The latest end date that can still produce a legal range, given the times
 * currently entered.
 *
 * A fixed `startDate + COMPILATION_MAX_RANGE_DAYS` ceiling is only correct when
 * the end time is no later in the day than the start time. With the form's
 * defaults (00:00 to 23:59) it is a day too generous, so the picker offered the
 * advertised maximum span and the form then rejected it — the user had to
 * shorten a selection the picker had just presented as valid.
 *
 * Deriving the bound from the actual times fixes that in both directions: it
 * yields `start + 3` while the end time is 23:59, and widens to `start + 4` as
 * soon as the end time is early enough for a full-length range to fit.
 */
export function latestEndDate({
  startDate,
  startTime,
  endTime,
  timezone,
  maxDate,
}: {
  readonly startDate: Date;
  readonly startTime: string;
  readonly endTime: string;
  readonly timezone: string;
  readonly maxDate?: Date | undefined;
}): Date {
  const startInstant = zonedInstant(startDate, startTime, timezone).getTime();

  let candidate = addDays(startDate, COMPILATION_MAX_RANGE_DAYS);
  while (
    candidate > startDate &&
    zonedInstant(candidate, endTime, timezone).getTime() - startInstant > COMPILATION_MAX_RANGE_MS
  ) {
    candidate = subDays(candidate, 1);
  }

  return maxDate ? earliestOf([candidate, maxDate]) : candidate;
}

/**
 * The earliest start date that can still produce a legal range, given the times
 * currently entered. The mirror of {@link latestEndDate}, and time-blind for
 * the same reason before this.
 */
export function earliestStartDate({
  endDate,
  startTime,
  endTime,
  timezone,
  minDate,
}: {
  readonly endDate: Date;
  readonly startTime: string;
  readonly endTime: string;
  readonly timezone: string;
  readonly minDate?: Date | undefined;
}): Date {
  const endInstant = zonedInstant(endDate, endTime, timezone).getTime();

  let candidate = subDays(endDate, COMPILATION_MAX_RANGE_DAYS);
  while (
    candidate < endDate &&
    endInstant - zonedInstant(candidate, startTime, timezone).getTime() > COMPILATION_MAX_RANGE_MS
  ) {
    candidate = addDays(candidate, 1);
  }

  return minDate ? latestOf([candidate, minDate]) : candidate;
}

/**
 * The camera and absolute window a completed form would submit.
 *
 * Named for what it is rather than for what it enables: the request is what
 * `POST /compilation` would receive, and the clip count is asked for the same
 * window so the two can never disagree about what was counted.
 */
export interface PendingCompilationRequest {
  readonly cameraId: string;
  readonly startTime: Date;
  readonly endTime: Date;
}

/** Whether a masked time input holds a complete, in-range `"HH:MM"`. */
export function isValidTime(time: string): boolean {
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) return false;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
}

/**
 * Resolves the form's fields into the request it would submit, or `null` while
 * the selection is incomplete or would be rejected.
 *
 * The single source of truth for "is this selection requestable" — the form's
 * `canSubmit`, and the clip count query, both read it, so neither can accept a
 * selection the other would refuse. The bounds mirror the backend's own refine
 * checks on `RequestCompilationBody`: strictly increasing, and no wider than
 * `COMPILATION_MAX_RANGE_MS`. Both are measured between instants, as the
 * backend measures them — four calendar days spanning a daylight-saving
 * change is 97 or 95 hours, not 96.
 */
export function pendingCompilationRequest(values: {
  readonly cameraId: string | null;
  readonly startDate: Date | null;
  readonly startTime: string;
  readonly endDate: Date | null;
  readonly endTime: string;
  /** The camera's timezone — what the entered dates and times mean. */
  readonly timezone: string;
}): PendingCompilationRequest | null {
  const { cameraId, startDate, startTime, endDate, endTime, timezone } = values;
  if (cameraId === null || startDate === null || endDate === null) return null;
  if (!isValidTime(startTime) || !isValidTime(endTime)) return null;

  const start = zonedInstant(startDate, startTime, timezone);
  const end = zonedInstant(endDate, endTime, timezone);
  const rangeMs = end.getTime() - start.getTime();
  if (rangeMs <= 0 || rangeMs > COMPILATION_MAX_RANGE_MS) return null;

  return { cameraId, startTime: start, endTime: end };
}
